import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Link2, Sparkles, RefreshCw, Wand2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  suggestInternalLinks, embedArticle, generateSeoKeywords,
  fetchAnchorCandidates, applyAnchorSuggestion, type AnchorCandidate,
} from "@/lib/contentOps/api";
import type { ArticleRow } from "@/lib/contentOps/types";

interface Props { article: ArticleRow; onSavePatch: (p: Partial<ArticleRow>) => Promise<void>; onReload?: () => Promise<void> | void }

const SeoTab = ({ article, onSavePatch, onReload }: Props) => {
  const [local, setLocal] = useState(article);
  const [suggestions, setSuggestions] = useState<{ slug: string; title: string; similarity: number }[]>([]);
  const [anchors, setAnchors] = useState<AnchorCandidate[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [loadingAnchors, setLoadingAnchors] = useState(false);
  const [applyingKey, setApplyingKey] = useState<string | null>(null);
  const [embedding, setEmbedding] = useState(false);
  const [suggestingKw, setSuggestingKw] = useState(false);

  const suggestKeywords = async () => {
    setSuggestingKw(true);
    try {
      const res = await generateSeoKeywords({ article_id: article.id });
      const all = Array.from(new Set([res.primary_keyword, ...res.secondary_keywords, ...res.long_tail_keywords].filter(Boolean)));
      sync({ target_keywords: all });
      await onSavePatch({ target_keywords: all });
      toast.success(`Suggested ${all.length} keywords`, { description: res.notes ?? undefined });
    } catch (e) {
      toast.error("Keyword suggestion failed", { description: (e as Error).message });
    } finally {
      setSuggestingKw(false);
    }
  };

  useEffect(() => { setLocal(article); }, [article.id]);

  const sync = (p: Partial<ArticleRow>) => setLocal({ ...local, ...p });

  const loadSuggestions = async () => {
    setLoadingLinks(true);
    try {
      const { suggestions } = await suggestInternalLinks(article.id, 8);
      setSuggestions(suggestions ?? []);
      if (!suggestions?.length) toast.info("No suggestions yet, embed the article first or publish more posts.");
    } catch (e) {
      toast.error("Couldn't load suggestions", { description: (e as Error).message });
    } finally {
      setLoadingLinks(false);
    }
  };

  const embed = async () => {
    setEmbedding(true);
    try {
      const res = await embedArticle(article.id, true);
      toast.success(res.skipped ? `Skipped: ${res.reason}` : "Embedding generated");
      await loadSuggestions();
    } catch (e) {
      toast.error("Embedding failed", { description: (e as Error).message });
    } finally {
      setEmbedding(false);
    }
  };

  const loadAnchors = async () => {
    setLoadingAnchors(true);
    try {
      const res = await fetchAnchorCandidates(article.id, 8);
      setSuggestions(res.suggestions ?? []);
      setAnchors(res.anchor_candidates ?? []);
      if (res.reason) toast.info(res.reason);
      else if (!res.anchor_candidates?.length) toast.info("No in-body anchor matches found in current paragraphs.");
    } catch (e) {
      toast.error("Couldn't compute anchor candidates", { description: (e as Error).message });
    } finally {
      setLoadingAnchors(false);
    }
  };

  const applyAnchor = async (c: AnchorCandidate) => {
    const key = `${c.section_index}.${c.paragraph_index}.${c.slug}`;
    setApplyingKey(key);
    try {
      const res = await applyAnchorSuggestion({ article_id: article.id, ...c });
      if (!res.ok) {
        toast.warning("Skipped, content changed", {
          description: res.message ?? res.reason ?? "Re-load candidates and try again.",
        });
        // Drop stale candidate from list
        setAnchors((prev) => prev.filter((x) =>
          !(x.section_index === c.section_index && x.paragraph_index === c.paragraph_index && x.slug === c.slug)));
      } else {
        toast.success(`Linked "${c.phrase}" → /${c.slug}`, { description: "Saved as revision" });
        setAnchors((prev) => prev.filter((x) =>
          !(x.section_index === c.section_index && x.paragraph_index === c.paragraph_index && x.slug === c.slug)));
        await onReload?.();
      }
    } catch (e) {
      toast.error("Apply failed", { description: (e as Error).message });
    } finally {
      setApplyingKey(null);
    }
  };

  const toggleSlug = (slug: string) => {
    const set = new Set(local.related_slugs ?? []);
    if (set.has(slug)) set.delete(slug); else set.add(slug);
    const arr = Array.from(set);
    sync({ related_slugs: arr });
    onSavePatch({ related_slugs: arr });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="space-y-4">
        <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: "hsl(0 0% 90%)" }}>SEO meta</h3>
          <div className="space-y-3">
            <div data-field="seo_title">
              <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(0 0% 60%)" }}>
                SEO title <span style={{ color: (local.seo_title?.length ?? 0) > 60 ? "hsl(0 75% 60%)" : "hsl(0 0% 45%)" }}>
                  ({local.seo_title?.length ?? 0}/60)
                </span>
              </label>
              <Input value={local.seo_title ?? ""} onChange={(e) => sync({ seo_title: e.target.value })}
                onBlur={() => onSavePatch({ seo_title: local.seo_title })}
                className="border" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
            </div>
            <div data-field="meta_description">
              <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(0 0% 60%)" }}>
                Meta description <span style={{ color: (local.meta_description?.length ?? 0) > 160 ? "hsl(0 75% 60%)" : "hsl(0 0% 45%)" }}>
                  ({local.meta_description?.length ?? 0}/160)
                </span>
              </label>
              <Textarea value={local.meta_description ?? ""} onChange={(e) => sync({ meta_description: e.target.value })}
                onBlur={() => onSavePatch({ meta_description: local.meta_description })} rows={3}
                className="border resize-none" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
            </div>
            <div data-field="slug">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium" style={{ color: "hsl(0 0% 60%)" }}>Slug</label>
                <Button size="sm" variant="ghost" onClick={() => {
                  const s = (article.title || "").toLowerCase().trim()
                    .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").slice(0, 80);
                  if (!s) { toast.info("Add a title first"); return; }
                  sync({ slug: s }); onSavePatch({ slug: s });
                }} className="h-6 px-2 text-[11px]" style={{ color: "hsl(207 68% 65%)" }}>
                  Derive from title
                </Button>
              </div>
              <Input value={local.slug ?? ""} onChange={(e) => sync({ slug: e.target.value })}
                onBlur={() => onSavePatch({ slug: local.slug })}
                className="border font-mono text-sm" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
            </div>
            <div data-field="target_keywords">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium" style={{ color: "hsl(0 0% 60%)" }}>Target keywords (comma-separated)</label>
                <Button size="sm" variant="ghost" onClick={suggestKeywords} disabled={suggestingKw}
                  className="h-6 px-2 text-[11px]" style={{ color: "hsl(207 68% 65%)" }}>
                  {suggestingKw ? <Loader2 size={11} className="animate-spin mr-1" /> : <Wand2 size={11} className="mr-1" />}
                  Suggest
                </Button>
              </div>
              <Input
                value={(local.target_keywords ?? []).join(", ")}
                onChange={(e) => sync({ target_keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                onBlur={() => onSavePatch({ target_keywords: local.target_keywords })}
                className="border" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
            </div>
          </div>
        </div>

        <div data-field="faqs" tabIndex={-1} className="rounded-xl border p-5 outline-none" style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: "hsl(0 0% 90%)" }}>FAQs</h3>
          <div className="space-y-3">
            {(local.faqs ?? []).map((f, i) => (
              <div key={i} className="rounded-lg border p-3 space-y-2" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 16%)" }}>
                <Input value={f.question} onChange={(e) => {
                  const next = [...local.faqs]; next[i] = { ...next[i], question: e.target.value }; sync({ faqs: next });
                }} onBlur={() => onSavePatch({ faqs: local.faqs })} placeholder="Question"
                  className="border text-sm font-medium" style={{ background: "hsl(0 0% 4%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 92%)" }} />
                <Textarea value={f.answer} onChange={(e) => {
                  const next = [...local.faqs]; next[i] = { ...next[i], answer: e.target.value }; sync({ faqs: next });
                }} onBlur={() => onSavePatch({ faqs: local.faqs })} rows={3} placeholder="Answer (40, 80 words)"
                  className="border resize-none text-sm" style={{ background: "hsl(0 0% 4%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)" }} />
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={() => sync({ faqs: [...(local.faqs ?? []), { question: "", answer: "" }] })}
              className="h-7 px-2 text-xs" style={{ color: "hsl(207 68% 60%)" }}>+ FAQ</Button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div data-field="related_slugs" tabIndex={-1} className="rounded-xl border p-5 outline-none" style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: "hsl(0 0% 90%)" }}>
              <Link2 size={14} /> Internal link suggestions
            </h3>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={embed} disabled={embedding || loadingLinks}
                className="h-7 px-2 text-xs" style={{ color: "hsl(207 68% 60%)" }}>
                {(embedding || loadingLinks) ? <Loader2 size={11} className="animate-spin mr-1" /> : <RefreshCw size={11} className="mr-1" />}
                Refresh suggestions
              </Button>
            </div>
          </div>

          {suggestions.length === 0 ? (
            <p className="text-xs py-4 text-center" style={{ color: "hsl(0 0% 50%)" }}>
              Click <strong>Refresh suggestions</strong> to compute semantic matches from your published articles.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {suggestions.map((s) => {
                const selected = (local.related_slugs ?? []).includes(s.slug);
                return (
                  <li key={s.slug}>
                    <button onClick={() => toggleSlug(s.slug)}
                      className="w-full text-left rounded-lg border p-2.5 flex items-center justify-between gap-2 transition-colors"
                      style={{
                        background: selected ? "hsl(207 68% 50% / 0.12)" : "hsl(0 0% 5%)",
                        borderColor: selected ? "hsl(207 68% 50% / 0.4)" : "hsl(0 0% 16%)",
                        color: "hsl(0 0% 90%)",
                      }}>
                      <div className="min-w-0">
                        <div className="text-sm line-clamp-1">{s.title}</div>
                        <div className="text-[10px]" style={{ color: "hsl(0 0% 50%)" }}>/{s.slug}</div>
                      </div>
                      <span className="text-[10px] rounded px-1.5 py-0.5"
                        style={{ background: "hsl(0 0% 12%)", color: "hsl(0 0% 70%)" }}>
                        {(s.similarity * 100).toFixed(0)}%
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="text-[11px] mt-3" style={{ color: "hsl(0 0% 40%)" }}>
            Selected links are saved to the article's <code>related_slugs</code>.
          </p>
        </div>

        <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: "hsl(0 0% 90%)" }}>
              <Wand2 size={14} /> In-body anchor candidates
            </h3>
            <Button size="sm" variant="ghost" onClick={loadAnchors} disabled={loadingAnchors}
              className="h-7 px-2 text-xs" style={{ color: "hsl(0 0% 70%)" }}>
              {loadingAnchors ? <Loader2 size={11} className="animate-spin mr-1" /> : <RefreshCw size={11} className="mr-1" />}
              Find candidates
            </Button>
          </div>
          {anchors.length === 0 ? (
            <p className="text-xs py-3" style={{ color: "hsl(0 0% 50%)" }}>
              Finds real phrases in your paragraphs that match other articles' titles or keywords.
              Apply inserts a link only at that exact position, manual edits are never overwritten.
            </p>
          ) : (
            <ul className="space-y-2">
              {anchors.map((c) => {
                const key = `${c.section_index}.${c.paragraph_index}.${c.slug}`;
                const isApplying = applyingKey === key;
                return (
                  <li key={key} className="rounded-lg border p-2.5"
                    style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 16%)" }}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-[11px] font-mono truncate" style={{ color: "hsl(207 68% 70%)" }}>
                        "{c.phrase}" → /{c.slug}
                      </div>
                      <span className="text-[10px] rounded px-1.5 py-0.5"
                        style={{ background: "hsl(0 0% 12%)", color: "hsl(0 0% 70%)" }}>
                        §{c.section_index + 1}·¶{c.paragraph_index + 1} · {(c.similarity * 100).toFixed(0)}%
                      </span>
                    </div>
                    <p className="text-[11px] mb-2 line-clamp-2" style={{ color: "hsl(0 0% 60%)" }}>
                      {c.paragraph_preview}
                    </p>
                    <Button size="sm" onClick={() => applyAnchor(c)} disabled={!!applyingKey}
                      className="h-6 px-2 text-[11px]"
                      style={{ background: "hsl(142 70% 35%)", color: "white" }}>
                      {isApplying ? <Loader2 size={11} className="animate-spin mr-1" /> : <Check size={11} className="mr-1" />}
                      Apply as revision
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="text-[11px] mt-3" style={{ color: "hsl(0 0% 40%)" }}>
            Each apply writes an <code>article_revisions</code> row and is rejected if the paragraph changed since loading.
          </p>
        </div>


        <div className="rounded-xl border p-5" style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: "hsl(0 0% 90%)" }}>Linked slugs</h3>
          {(local.related_slugs ?? []).length === 0 ? (
            <p className="text-xs" style={{ color: "hsl(0 0% 50%)" }}>No internal links yet.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {(local.related_slugs ?? []).map((s) => (
                <span key={s} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]"
                  style={{ background: "hsl(207 68% 50% / 0.15)", color: "hsl(207 68% 75%)" }}>
                  /{s}
                  <button onClick={() => {
                    const arr = (local.related_slugs ?? []).filter((x) => x !== s);
                    sync({ related_slugs: arr }); onSavePatch({ related_slugs: arr });
                  }} className="hover:text-white">×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeoTab;
